import React from 'react';
import { 
  Button, 
  ButtonGroup, 
  useColorModeValue, 
  Box, 
  useBreakpointValue 
} from '@chakra-ui/react';
import { FiList } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react'; // Import FullCalendar types if needed for ref

// Define CalendarView type here or import from a shared location
type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek';

interface ViewToggleButtonsProps {
  calendarView: CalendarView;
  setCalendarView: (view: CalendarView) => void;
  calendarRef: React.RefObject<FullCalendar | null>;
}

export const ViewToggleButtons: React.FC<ViewToggleButtonsProps> = ({ 
  calendarView, 
  setCalendarView, 
  calendarRef 
}) => {
  const { t } = useTranslation(['v0/calendar']);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const changeView = (view: CalendarView) => {
    setCalendarView(view);
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(view);
    }
  };

  return (
    <ButtonGroup 
      size={isMobile ? "xs" : "sm"}
      spacing={isMobile ? 0 : 1} 
      bg={useColorModeValue('white', 'gray.800')}
      p={isMobile ? 0.5 : 1}
      borderRadius="full"
      boxShadow="sm"
      flexWrap={isMobile ? "nowrap" : "nowrap"}
    >
      <Button
        onClick={() => changeView('dayGridMonth')}
        colorScheme="pink"
        variant={calendarView === 'dayGridMonth' ? 'solid' : 'ghost'}
        borderRadius="full"
        size={isMobile ? "xs" : "sm"}
        px={isMobile ? 2 : 4}
        _hover={{
          bg: calendarView === 'dayGridMonth' ? 'pink.600' : useColorModeValue('pink.50', 'whiteAlpha.200'),
        }}
      >
        {isMobile ? t('v0/calendar:monthShort') : t('v0/calendar:month')}
      </Button>
      <Button
        onClick={() => changeView('timeGridWeek')}
        colorScheme="pink"
        variant={calendarView === 'timeGridWeek' ? 'solid' : 'ghost'}
        borderRadius="full"
        size={isMobile ? "xs" : "sm"}
        px={isMobile ? 2 : 4}
        _hover={{
          bg: calendarView === 'timeGridWeek' ? 'pink.600' : useColorModeValue('pink.50', 'whiteAlpha.200'),
        }}
      >
        {isMobile ? t('v0/calendar:weekShort') : t('v0/calendar:week')}
      </Button>
      <Button
        onClick={() => changeView('timeGridDay')}
        colorScheme="pink"
        variant={calendarView === 'timeGridDay' ? 'solid' : 'ghost'}
        borderRadius="full"
        size={isMobile ? "xs" : "sm"}
        px={isMobile ? 2 : 4}
        _hover={{
          bg: calendarView === 'timeGridDay' ? 'pink.600' : useColorModeValue('pink.50', 'whiteAlpha.200'),
        }}
      >
        {isMobile ? t('v0/calendar:dayShort') : t('v0/calendar:day')}
      </Button>
      <Button
        onClick={() => changeView('listWeek')}
        colorScheme="pink"
        variant={calendarView === 'listWeek' ? 'solid' : 'ghost'}
        borderRadius="full"
        size={isMobile ? "xs" : "sm"}
        px={isMobile ? 2 : 4}
        leftIcon={isMobile ? undefined : <Box as={FiList} size="1em" />}
        _hover={{
          bg: calendarView === 'listWeek' ? 'pink.600' : useColorModeValue('pink.50', 'whiteAlpha.200'),
        }}
      >
        {isMobile ? (
          <Box as={FiList} size="1em" />
        ) : (
          t('v0/calendar:timeline') // Assuming 'timeline' corresponds to 'listWeek' translation
        )}
      </Button>
    </ButtonGroup>
  );
}; 